import { createTheme, Theme, ThemeOptions, ThemeProvider } from '@mui/material';
import type { TypographyOptions, TypographyUtils } from '@mui/material/styles/createTypography';
import { deepmerge } from '@mui/utils';
import React, { createContext } from 'react';
import { useThemeSettings } from './hooks/use-theme-settings';
import { RTLCacheProvider } from './rtl-cache-provider';
import type { ThemeProviderProps } from './theme-types';

declare module '@mui/material/styles' {
	interface Palette {
		backdropAlpha: number;
	}
	interface PaletteOptions {
		backdropAlpha?: number;
	}
}

export type ExtendedTypographyOptions = TypographyOptions & Partial<TypographyUtils>;

export type ExtendedThemeOptions = ThemeOptions & {
	typography?: ExtendedTypographyOptions;
};

export const TOOLTIP_FONTSIZE_PX = 12;

const HTML_FONTSIZE_PX = 10;
function pxToRem(px: number): string {
	return `${px / HTML_FONTSIZE_PX}rem`;
}

const DEFAULT_BASE_THEME_OPTIONS: ExtendedThemeOptions = {
	typography: {
		fontFamily: 'Open Sans, Arial, sans-serif',
		htmlFontSize: HTML_FONTSIZE_PX,
		pxToRem: pxToRem,
		allVariants: {
			letterSpacing: 'normal'
		}
	},
	palette: {
		backdropAlpha: 0.3,
		warning: {
			// light: orange[500],
			main: '#ed6c02' //TODO: update @mui and check if its still needed
		}
	},
	mixins: {
		toolbar: {
			minHeight: 40
		}
	},
	shape: {
		borderRadius: 0
	},
	components: {
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					fontSize: pxToRem(TOOLTIP_FONTSIZE_PX)
				}
			}
		}
	}
};

export const ThemeOptionsContext = createContext<ExtendedThemeOptions>(DEFAULT_BASE_THEME_OPTIONS);

export function MAEDefaultThemeProvider({ children }: ThemeProviderProps): JSX.Element {
	const [{ store: themeSettingsStore }] = useThemeSettings();
	const [maeDefaultThemeOptions, setDefaultThemeOptions] = React.useState<ExtendedThemeOptions>(
		deepmerge(DEFAULT_BASE_THEME_OPTIONS, themeSettingsStore.themeOptions as ExtendedThemeOptions)
	);
	const [maeDefaultTheme, setMaeDefaultTheme] = React.useState<Theme>(createTheme(maeDefaultThemeOptions));

	React.useEffect(() => {
		setDefaultThemeOptions(deepmerge(DEFAULT_BASE_THEME_OPTIONS, themeSettingsStore.themeOptions as ExtendedThemeOptions));
	}, [themeSettingsStore, themeSettingsStore.themeOptions]);

	React.useEffect(() => {
		setMaeDefaultTheme(createTheme(maeDefaultThemeOptions));
	}, [maeDefaultThemeOptions]);

	return (
		<ThemeOptionsContext.Provider value={maeDefaultThemeOptions}>
			<RTLCacheProvider direction={maeDefaultTheme.direction}>
				<ThemeProvider theme={maeDefaultTheme}>{children}</ThemeProvider>
			</RTLCacheProvider>
		</ThemeOptionsContext.Provider>
	);
}
